import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BarraDeEtapa } from "@/components/ui/barra-de-etapa";
import { ChipDeAtencion } from "@/components/ui/chip-de-atencion";
import { ChipPendiente } from "@/components/ui/chip-pendiente";
import { Etiqueta, type TonoDeEtiqueta } from "@/components/ui/etiqueta";
import { FiltroDePersona } from "@/components/ui/filtro-de-persona";
import { FiltroSegmentado } from "@/components/ui/filtro-segmentado";
import { PilaDeAvatares } from "@/components/ui/pila-de-avatares";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * Catálogo de lo que las pantallas con diseño (Claude Design) necesitan y el DS
 * base no traía: tokens de tinta y superficie, tipografía de apoyo, etiquetas de
 * estado, filtro segmentado, pestañas de línea negra, botón fuerte y card marcada.
 * Vive en `/design-system` como el resto del catálogo: es el QA visual de estas
 * piezas, y la prueba de que existen para que la siguiente pantalla no las
 * reinvente.
 */

const ETAPAS = ["Creado", "Terreno", "Antecedentes", "Generación", "En visado", "Enviado"];

const EQUIPO = [
  { rol: "Ejecutivo a cargo", persona: { id: 1, nombre: "Ariel Guzman" } },
  { rol: "Encargado de terreno", persona: { id: 2, nombre: "Fernando Schmidt" }, leToca: true },
  { rol: "Revisor de la cotización", persona: { id: 3, nombre: "Diego Perez" } },
  { rol: "Revisor del informe final", persona: null },
];

const TONOS: [TonoDeEtiqueta, string][] = [
  ["neutral", "Faltante"],
  ["tenue", "Pendiente"],
  ["success", "Validada"],
  ["warning", "Propuesta"],
  ["info", "Editada"],
  ["danger", "Bloquea"],
  ["desactualizada", "Desactualizada"],
];

/* [clase de fondo, nombre del token, hex claro]. Las clases se escriben completas
   para que Tailwind las encuentre; armarlas con una plantilla no las genera. */
const TINTAS: [string, string, string][] = [
  ["bg-ink-secundario", "ink-secundario", "#43454A"],
  ["bg-ink-medio", "ink-medio", "#71757B"],
  ["bg-superficie-tenue border", "superficie-tenue", "#FAFAFA"],
  ["bg-divisor-fuerte border", "divisor-fuerte", "#F1F1F0"],
  ["bg-danger-texto", "danger-texto", "#A8322B"],
  ["bg-danger-texto-fuerte", "danger-texto-fuerte", "#7D2620"],
  ["bg-warning-texto", "warning-texto", "#8A5A12"],
  ["bg-desactualizada-soft border", "desactualizada-soft", "#FDE7D6"],
  ["bg-fila-anticipo border", "fila-anticipo", "#FFFBF3"],
  ["bg-panel-anticipo border", "panel-anticipo", "#FDF8EF"],
  ["bg-desactualizada-foreground", "desactualizada-foreground", "#A14A12"],
  ["bg-desactualizada-fuerte", "desactualizada-fuerte", "#6B300B"],
];

function Rotulo({ children }: { children: React.ReactNode }) {
  return <div className="rotulo-seccion mb-3.5">{children}</div>;
}

export function PantallasConDiseno() {
  const [filtro, setFiltro] = useState("abiertas");
  const [atencion, setAtencion] = useState<string | null>("atrasados");
  const [persona, setPersona] = useState("");
  const [rol, setRol] = useState("cualquiera");

  return (
    <div className="flex flex-col gap-5" data-testid="pantallas-con-diseno">
      <Card className="gap-0 p-7">
        <Rotulo>Tinta y superficie nuevas</Rotulo>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {TINTAS.map(([clase, nombre, hex]) => (
            <div key={nombre}>
              <div className={`h-12 rounded-xl ${clase}`} />
              <div className="mt-1.5 text-xs font-bold">{nombre}</div>
              <div className="text-ink-medio font-mono text-[0.625rem]">{hex}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="gap-0 p-7">
        <Rotulo>Tipografía de apoyo</Rotulo>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <p className="text-meta text-ink-secundario">
              text-meta · 13px/600, sin tracking: metadatos, ayudas y pies de fila.
            </p>
            <p className="text-caption text-ink-medio mt-1">
              text-caption · 12px/600. Tampoco lleva tracking.
            </p>
          </div>
          <div>
            <p className="rotulo-seccion">rotulo-seccion · 10,5px/800/.07em</p>
            <p className="text-ink-medio mt-2 max-w-prose text-xs">
              <b className="text-ink-body">text-label</b> es un RÓTULO en versalitas: usado como
              texto corrido lo deja con la separación de letras de un título. Para texto
              corrido, <b className="text-ink-body">text-meta</b> o <b className="text-ink-body">text-caption</b>.
            </p>
          </div>
        </div>
      </Card>

      <Card className="gap-0 p-7">
        <Rotulo>Etiqueta de estado y de tipo</Rotulo>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {TONOS.map(([tono, texto]) => (
            <Etiqueta key={tono} tono={tono}>
              {texto}
            </Etiqueta>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {TONOS.map(([tono, texto]) => (
            <Etiqueta key={tono} tono={tono} size="sm">
              {texto}
            </Etiqueta>
          ))}
        </div>
        <p className="text-meta text-ink-medio mt-3">
          md (10,5px) para el detalle, sm (10px) para las filas de una lista.
        </p>
      </Card>

      <div className="grid gap-5 md:grid-cols-2">
        <Card className="gap-0 p-7">
          <Rotulo>Filtro segmentado</Rotulo>
          <FiltroSegmentado
            aria-label="Filtrar dudas"
            valor={filtro}
            onCambio={setFiltro}
            opciones={[
              { valor: "abiertas", etiqueta: "Abiertas", cantidad: 5 },
              { valor: "bloquean", etiqueta: "Bloquean", cantidad: 2 },
              { valor: "respondidas", etiqueta: "Respondidas", cantidad: 3 },
              { valor: "todas", etiqueta: "Todas", cantidad: 8 },
            ]}
          />
        </Card>
        <Card className="gap-0 p-7">
          <Rotulo>Pestañas con línea negra</Rotulo>
          <Tabs variant="underline-ink" defaultValue="dudas">
            <TabsList>
              <TabsTrigger value="dudas">
                Dudas
                <span className="text-ink-medio font-mono text-[0.6875rem] font-bold">8</span>
              </TabsTrigger>
              <TabsTrigger value="hechos">
                Hoja de hechos
                <span className="text-ink-medio font-mono text-[0.6875rem] font-bold">15</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <p className="text-meta text-ink-medio mt-3">
            Las pestañas del catálogo (arriba) conservan la línea verde.
          </p>
        </Card>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <Card className="gap-0 p-7">
          <Rotulo>Botón fuerte</Rotulo>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="fuerte">Responder dudas</Button>
            <Button variant="fuerte" disabled>
              Guardar respuesta
            </Button>
          </div>
          <p className="text-meta text-ink-medio mt-3">
            Verde profundo con halo; deshabilitado gris pleno, sin opacidad.
          </p>
        </Card>
        <div className="grid grid-cols-2 gap-3">
          <Card className="gap-0 p-5">
            <p className="text-sm font-bold">Card</p>
            <p className="text-meta text-ink-medio">borde de 1 px (defecto)</p>
          </Card>
          <Card variant="marcada" className="gap-0 p-5">
            <p className="text-sm font-bold">Card marcada</p>
            <p className="text-meta text-ink-medio">borde de 1,5 px</p>
          </Card>
        </div>
      </div>

      <Card className="gap-0 p-7">
        <Rotulo>Barra de etapa</Rotulo>
        <div className="grid gap-5 sm:grid-cols-3">
          <BarraDeEtapa etapas={ETAPAS} indice={0} etiqueta="Enviada" />
          <BarraDeEtapa etapas={ETAPAS} indice={2} />
          <BarraDeEtapa etapas={ETAPAS} indice={5} />
        </div>
        <p className="text-meta text-ink-medio mt-3">
          Una secuencia: hechas en verde, la actual en tinta, el resto en el borde. Al terminar
          todo va en verde y la cuenta se va.
        </p>
      </Card>

      <div className="grid gap-5 md:grid-cols-2">
        <Card className="gap-0 p-7">
          <Rotulo>Chip de atención</Rotulo>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["atrasados", "danger", "Atrasados", 4],
                ["novedades", "info", "Novedades de la app", 3],
                ["dudas", "warning", "Dudas de la IA", 2],
              ] as const
            ).map(([clave, tono, texto, n]) => (
              <ChipDeAtencion
                key={clave}
                tono={tono}
                cantidad={n}
                activo={atencion === clave}
                onClick={() => setAtencion(atencion === clave ? null : clave)}
              >
                {texto}
              </ChipDeAtencion>
            ))}
          </div>
          <p className="text-meta text-ink-medio mt-3">
            Interruptor (aria-pressed) que filtra lo que pide mirar hoy.
          </p>
        </Card>
        <Card className="gap-0 p-7">
          <Rotulo>Chip de pendiente</Rotulo>
          <div className="flex flex-wrap gap-1.5">
            <ChipPendiente tono="danger">Atrasado 10 días</ChipPendiente>
            <ChipPendiente tono="info">App · 3 actualizaciones</ChipPendiente>
            <ChipPendiente tono="warning">2 dudas de la IA</ChipPendiente>
          </div>
          <p className="text-meta text-ink-medio mt-3">
            Lo que una fila tiene por resolver; es un botón que lleva a resolverlo.
          </p>
        </Card>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <Card className="gap-0 p-7">
          <Rotulo>Pila de avatares</Rotulo>
          <PilaDeAvatares equipo={EQUIPO} nombre="Equipo del ejemplo" />
          <p className="text-meta text-ink-medio mt-3">
            Personas únicas; con el turno primero y anillo verde. Apunta o enfoca para ver los
            roles.
          </p>
        </Card>
        <Card className="gap-0 p-7">
          <Rotulo>Filtro de persona y rol</Rotulo>
          <FiltroDePersona
            personas={[
              { valor: "1", etiqueta: "Yo" },
              { valor: "2", etiqueta: "Fernando Schmidt" },
            ]}
            persona={persona}
            onPersona={setPersona}
            roles={[
              { valor: "cualquiera", etiqueta: "en cualquier rol" },
              { valor: "le_toca", etiqueta: "a quien le toca ahora" },
            ]}
            rol={rol}
            onRol={setRol}
          />
          <p className="text-meta text-ink-medio mt-3">
            Se lee como una frase y se marca con una persona elegida; el rol espera a que la haya.
          </p>
        </Card>
      </div>
    </div>
  );
}
